import React, { useRef, useState } from "react";
import moment from "moment";
import api from "../../api";
import { Modal } from "react-bootstrap";
import { notify } from "../../utils/toast";
import logo from "../../assets/images/logo.png";

export const FeeReceipts = () => {
  const [receiptNo, setReceiptNo] = useState("");
  const [rollNo, setRollNo] = useState("");
  const [loader, setLoader] = useState(false);
  const [results, setResults] = useState(null); // null = not searched yet
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    const rNo = receiptNo.trim().toUpperCase();
    const roll = rollNo.trim();

    if (!rNo && !roll) {
      notify.error("Enter a receipt number or roll number to search.");
      return;
    }

    try {
      setLoader(true);
      setResults(null);
      const params = {};
      if (rNo) params.receiptNo = rNo;
      if (roll) params.rollNo = roll;

      const res = await api.searchFeePayments(params);
      if (res?.success) {
        setResults(res.payments || []);
      }
    } catch (err) {
      notify.error(err?.message || "No records found.");
      setResults([]);
    } finally {
      setLoader(false);
    }
  };

  const handleClear = () => {
    setReceiptNo("");
    setRollNo("");
    setResults(null);
  };

  const openReceipt = (payment) => {
    setSelected(payment);
    setShowModal(true);
  };

  return (
    <div className="DashboardMain">
      <div className="row">
        <div className="col">
          <h3 className="mb-4">Fee Receipt Search</h3>

          {/* Search Card */}
          <div
            className="p-4 mb-4 rounded"
            style={{
              background: "#fff",
              border: "1px solid #e5e7eb",
              maxWidth: 640,
            }}
          >
            <form onSubmit={handleSearch}>
              <div className="row g-3">
                <div className="col-md-5">
                  <label
                    className="form-label fw-bold"
                    style={{ fontSize: 13 }}
                  >
                    Receipt Number
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. RCP-A1B2C3"
                    value={receiptNo}
                    onChange={(e) => setReceiptNo(e.target.value)}
                  />
                </div>

                <div
                  className="col-md-1 d-flex align-items-end justify-content-center pb-1"
                  style={{ fontSize: 13, color: "#888", fontWeight: 600 }}
                >
                  OR
                </div>

                <div className="col-md-4">
                  <label
                    className="form-label fw-bold"
                    style={{ fontSize: 13 }}
                  >
                    Roll Number
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. CS-101"
                    value={rollNo}
                    onChange={(e) => setRollNo(e.target.value)}
                  />
                </div>

                <div className="col-md-2 d-flex align-items-end gap-2">
                  <button
                    type="submit"
                    className="btn btn-dark w-100"
                    disabled={loader}
                  >
                    {loader ? (
                      <i className="fa-solid fa-spinner fa-spin"></i>
                    ) : (
                      <i className="fa-solid fa-magnifying-glass"></i>
                    )}
                  </button>
                  {results !== null && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary w-100"
                      onClick={handleClear}
                      title="Clear"
                    >
                      <i className="fa-solid fa-xmark"></i>
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>

          {/* Results */}
          {results !== null && (
            <>
              <div className="mb-2 text-muted" style={{ fontSize: 13 }}>
                {results.length > 0
                  ? `${results.length} record${results.length > 1 ? "s" : ""} found`
                  : "No records found."}
              </div>

              {results.length > 0 && (
                <div className="TableMain mt-0">
                  <div className="table-responsive">
                    <table className="table table-hover drivers-table">
                      <thead>
                        <tr>
                          <th>Receipt No.</th>
                          <th>Student</th>
                          <th>Roll No.</th>
                          <th>Date</th>
                          <th>Method</th>
                          <th>Amount (PKR)</th>
                          <th>Fee Status</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.map((p) => {
                          const fee = p.feeId;
                          const student = fee?.studentId || p.studentId;
                          const statusMap = {
                            paid: "bg-success",
                            partial: "bg-warning text-dark",
                            unpaid: "bg-danger",
                          };
                          return (
                            <tr key={p._id}>
                              <td>
                                <span
                                  className="fw-bold"
                                  style={{
                                    fontFamily: "monospace",
                                    background: "#f0f3ff",
                                    padding: "2px 8px",
                                    borderRadius: 6,
                                    color: "#1a1f5e",
                                  }}
                                >
                                  {p.receiptNo}
                                </span>
                              </td>
                              <td>
                                <div className="table-text-dark fw-600">
                                  {student?.name || "—"}
                                </div>
                              </td>
                              <td>
                                <span className="table-text-dark">
                                  {student?.rollNo || "—"}
                                </span>
                              </td>
                              <td>
                                <span className="table-text-dark">
                                  {moment(p.createdAt).format("DD MMM YYYY")}
                                </span>
                                <small className="text-muted d-block">
                                  {moment(p.createdAt).format("hh:mm A")}
                                </small>
                              </td>
                              <td>
                                <span
                                  className="table-text-dark"
                                  style={{ textTransform: "capitalize" }}
                                >
                                  {p.method}
                                </span>
                              </td>
                              <td>
                                <strong>{p.amount?.toLocaleString()}</strong>
                              </td>
                              <td>
                                <span
                                  className={`badge ${statusMap[fee?.status] || "bg-secondary"} text-uppercase`}
                                >
                                  {fee?.status || "—"}
                                </span>
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => openReceipt(p)}
                                >
                                  <i className="fa-solid fa-receipt me-1"></i>
                                  Receipt
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {selected && showModal && (
        <SingleReceiptModal
          show={showModal}
          handleClose={() => {
            setShowModal(false);
            setSelected(null);
          }}
          payment={selected}
        />
      )}
    </div>
  );
};

/* ─── Shared print styles ─── */
const PRINT_STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; background: #fff; color: #222; }
  .receipt-wrapper { width: 700px; margin: 30px auto; padding: 40px; border: 2px solid #10b981; border-radius: 12px; }
  .receipt-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #10b981; padding-bottom: 20px; margin-bottom: 24px; }
  .receipt-header img { height: 60px; }
  .receipt-header-right { text-align: right; }
  .receipt-header-right h2 { color: #10b981; font-size: 22px; margin-bottom: 4px; }
  .receipt-header-right p { font-size: 12px; color: #666; }
  .receipt-title { text-align: center; font-size: 20px; font-weight: bold; letter-spacing: 2px; color: #10b981; margin-bottom: 24px; text-transform: uppercase; }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 32px; margin-bottom: 24px; }
  .info-item label { font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 1px; display: block; margin-bottom: 2px; }
  .info-item span { font-size: 14px; font-weight: 600; color: #222; }
  .section-title { font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #10b981; margin-bottom: 10px; }
  .payment-table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
  .payment-table th { background: #10b981; color: white; padding: 10px 14px; text-align: left; font-size: 13px; }
  .payment-table td { padding: 10px 14px; border-bottom: 1px solid #e5e7eb; font-size: 13px; }
  .payment-table tr:last-child td { border-bottom: none; }
  .summary-box { background: #f0fdf4; border: 1px solid #10b981; border-radius: 8px; padding: 16px 20px; margin-bottom: 24px; }
  .summary-row { display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 14px; }
  .summary-row:last-child { margin-bottom: 0; border-top: 1px dashed #10b981; padding-top: 8px; font-weight: bold; font-size: 15px; }
  .status-badge { display: inline-block; padding: 4px 14px; border-radius: 20px; font-size: 12px; font-weight: bold; text-transform: uppercase; }
  .status-paid    { background: #d1fae5; color: #065f46; }
  .status-partial { background: #fef3c7; color: #92400e; }
  .status-unpaid  { background: #fee2e2; color: #991b1b; }
  .receipt-footer { text-align: center; font-size: 11px; color: #aaa; border-top: 1px solid #e5e7eb; padding-top: 16px; margin-top: 8px; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
`;

/* ─── Single Payment Receipt Modal ─── */
const SingleReceiptModal = ({ show, handleClose, payment }) => {
  const receiptRef = useRef();
  const fee = payment?.feeId;
  const student = fee?.studentId || payment?.studentId;
  const statusClass =
    {
      paid: "status-paid",
      partial: "status-partial",
      unpaid: "status-unpaid",
    }[fee?.status] || "";

  const handlePrint = () => {
    const w = window.open("", "_blank", "width=800,height=600");
    w.document.write(
      `<!DOCTYPE html><html><head><title>Receipt ${payment.receiptNo}</title><style>${PRINT_STYLES}</style></head><body>${receiptRef.current.innerHTML}</body></html>`,
    );
    w.document.close();
    w.focus();
    setTimeout(() => {
      w.print();
      w.close();
    }, 300);
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <div className="taskForm" style={{ maxWidth: "100%" }}>
        <div className="closeIcon" onClick={handleClose}>
          <i className="fa-solid fa-xmark" />
        </div>

        <div className="d-flex justify-content-end gap-2 mb-3">
          <button className="btn btn-success" onClick={handlePrint}>
            <i className="fa-solid fa-print me-2"></i>Print / Save PDF
          </button>
        </div>

        <div ref={receiptRef}>
          <div className="receipt-wrapper">
            {/* Header */}
            <div className="receipt-header">
              <img src={logo} alt="Logo" style={{ height: 60 }} />
              <div className="receipt-header-right">
                <h2 style={{ color: "#10b981", marginBottom: 4 }}>
                  Shabbir Transport
                </h2>
                <p style={{ fontSize: 12, color: "#666" }}>
                  Receipt No: <strong>{payment.receiptNo}</strong>
                </p>
                <p style={{ fontSize: 12, color: "#666" }}>
                  Printed: {moment().format("DD MMM YYYY, hh:mm A")}
                </p>
              </div>
            </div>

            <div className="receipt-title">Payment Receipt</div>

            {/* Student info */}
            <div className="info-grid">
              <div className="info-item">
                <label>Student Name</label>
                <span>{student?.name || "—"}</span>
              </div>
              <div className="info-item">
                <label>Roll Number</label>
                <span>{student?.rollNo || "—"}</span>
              </div>
              <div className="info-item">
                <label>Fee Plan</label>
                <span style={{ textTransform: "capitalize" }}>
                  {fee?.plan || "—"}
                </span>
              </div>
              <div className="info-item">
                <label>Due Date</label>
                <span>
                  {fee?.dueDate
                    ? moment(fee.dueDate).format("DD MMM YYYY")
                    : "—"}
                </span>
              </div>
              <div className="info-item">
                <label>Fee Status</label>
                <span className={`status-badge ${statusClass}`}>
                  {fee?.status || "—"}
                </span>
              </div>
              <div className="info-item">
                <label>Route</label>
                <span>{student?.routeId?.name || "—"}</span>
              </div>
            </div>

            {/* Payment row */}
            <div className="section-title">Payment Detail</div>
            <table className="payment-table">
              <thead>
                <tr>
                  <th>Receipt No.</th>
                  <th>Date &amp; Time</th>
                  <th>Method</th>
                  <th>Amount (PKR)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <strong>{payment.receiptNo}</strong>
                  </td>
                  <td>
                    {moment(payment.createdAt).format("DD MMM YYYY, hh:mm A")}
                  </td>
                  <td style={{ textTransform: "capitalize" }}>
                    {payment.method}
                  </td>
                  <td>
                    <strong>{payment.amount?.toLocaleString()}</strong>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Summary */}
            <div className="summary-box">
              <div className="summary-row">
                <span>Total Fee</span>
                <span>PKR {fee?.amount?.toLocaleString()}</span>
              </div>
              <div className="summary-row">
                <span>This Payment</span>
                <span style={{ color: "#10b981" }}>
                  PKR {payment.amount?.toLocaleString()}
                </span>
              </div>
              <div className="summary-row">
                <span>Total Paid</span>
                <span style={{ color: "#10b981" }}>
                  PKR {fee?.paidAmount?.toLocaleString()}
                </span>
              </div>
              <div className="summary-row">
                <span>Balance Remaining</span>
                <span
                  style={{
                    color:
                      fee?.amount - fee?.paidAmount > 0 ? "#dc2626" : "#10b981",
                  }}
                >
                  PKR {(fee?.amount - fee?.paidAmount)?.toLocaleString()}
                </span>
              </div>
            </div>

            <div className="receipt-footer">
              <p>
                This is a computer-generated receipt. No signature required.
              </p>
              <p style={{ marginTop: 4 }}>
                Shabbir Transport &mdash; Thank you for your payment
              </p>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
